import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { motion } from 'motion/react';
import {
  CheckCircle,
  ArrowLeft,
  ArrowRight,
  Eye,
  Play,
  Download,
  Save,
  Edit3,
  Presentation,
  Clock,
  Users,
  BookOpen,
  Loader2 } from
'lucide-react';

interface Slide {
  id: string;
  title: string;
  content: string;
  type: string;
  notes: string;
  imageKeywords: string;
  interactiveElement?: string;
}

interface ContentApprovalProps {
  presentationData: any;
  updatePresentationData: (updates: any) => void;
  onNext: () => void;
  onPrevious: () => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
}

const ContentApproval: React.FC<ContentApprovalProps> = ({
  presentationData,
  updatePresentationData,
  onNext,
  onPrevious,
  isLoading,
  setIsLoading
}) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isApproved, setIsApproved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();

  const slides: Slide[] = presentationData.content || [];
  const currentSlide = slides[currentSlideIndex];

  const estimatedDuration = slides.length * 2; // 2 minutes per slide average

  // Helper function to safely get text content and word count
  const getTextContent = (content: any): string => {
    if (typeof content === 'string') {
      return content;
    }
    if (typeof content === 'object' && content !== null) {
      // If content is an object, try to extract text from it
      if (content.text) return content.text;
      if (content.content) return content.content;
      // If it's an array, join the elements
      if (Array.isArray(content)) {
        return content.map((item) => typeof item === 'string' ? item : JSON.stringify(item)).join(' ');
      }
      // Otherwise convert to string
      return JSON.stringify(content);
    }
    return '';
  };

  const wordCount = slides.reduce((total, slide) => {
    const slideContent = getTextContent(slide.content);
    const slideTitle = getTextContent(slide.title);
    return total + slideContent.split(' ').length + slideTitle.split(' ').length;
  }, 0);

  const handleApproveAndContinue = () => {
    if (!slides || slides.length === 0) {
      toast({
        title: "No Content",
        description: "There are no slides to approve. Please go back and generate content.",
        variant: "destructive"
      });
      return;
    }

    setIsApproved(true);
    updatePresentationData({
      approvedContent: slides,
      approvedAt: new Date().toISOString()
    });

    toast({
      title: "Content Approved!",
      description: "Your presentation content has been approved and is ready for design."
    });

    setTimeout(() => {
      onNext();
    }, 1000);
  };

  const handleSavePresentation = async () => {
    if (!slides || slides.length === 0) return;

    setIsSaving(true);
    try {
      // Here you would implement saving to database
      // For now, we'll just simulate the save
      await new Promise((resolve) => setTimeout(resolve, 2000));

      toast({
        title: "Presentation Saved",
        description: "Your presentation has been saved successfully."
      });
    } catch (error) {
      toast({
        title: "Save Failed",
        description: "Failed to save presentation. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleExportSummary = () => {
    const summary = `Presentation Summary
Title: ${presentationData.title}
Topic: ${presentationData.topic}
Subject: ${presentationData.subject}
Grade Level: ${presentationData.gradeLevel}
Number of Slides: ${slides.length}
Estimated Duration: ${estimatedDuration} minutes
Word Count: ${wordCount} words

Slides Overview:
${slides.map((slide, index) => {
      const slideContent = getTextContent(slide.content);
      const slideNotes = getTextContent(slide.notes);
      return `
${index + 1}. ${getTextContent(slide.title)} (${slide.type})
   Content: ${slideContent.substring(0, 100)}...
   Notes: ${slideNotes.substring(0, 80)}...
`;
    }).join('\n')}

Generated on: ${new Date().toLocaleDateString()}`;

    const blob = new Blob([summary], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${presentationData.title || 'presentation'}-summary.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast({
      title: "Summary Exported",
      description: "Presentation summary has been downloaded."
    });
  };

  if (!slides || slides.length === 0) {
    return (
      <div className="text-center py-12" data-id="jd9a9lk3q" data-path="src/components/ai/ContentApproval.tsx">
        <div className="max-w-md mx-auto" data-id="qy5u4ap64" data-path="src/components/ai/ContentApproval.tsx">
          <CheckCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" data-id="uni1t1ikr" data-path="src/components/ai/ContentApproval.tsx" />
          <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="9ybkzsyc7" data-path="src/components/ai/ContentApproval.tsx">No Content to Review</h3>
          <p className="text-gray-600 mb-6" data-id="p9lotrac5" data-path="src/components/ai/ContentApproval.tsx">
            Go back to the previous steps to generate and edit content first.
          </p>
          <Button onClick={onPrevious} variant="outline" data-id="z5ztcp5ij" data-path="src/components/ai/ContentApproval.tsx">
            <ArrowLeft className="h-4 w-4 mr-2" data-id="yxaccnlbc" data-path="src/components/ai/ContentApproval.tsx" />
            Back to Content Editor
          </Button>
        </div>
      </div>);

  }

  return (
    <div className="space-y-6" data-id="dt974lo42" data-path="src/components/ai/ContentApproval.tsx">
      {/* Header */}
      <div className="text-center" data-id="ixok98var" data-path="src/components/ai/ContentApproval.tsx">
        <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="pztgzgagm" data-path="src/components/ai/ContentApproval.tsx">
          Review Your Presentation
        </h3>
        <p className="text-gray-600" data-id="mqk58ip6o" data-path="src/components/ai/ContentApproval.tsx">
          Review your content before finalizing the presentation design
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="vyogz5t2b" data-path="src/components/ai/ContentApproval.tsx">
        <Card data-id="507qi1p1l" data-path="src/components/ai/ContentApproval.tsx">
          <CardContent className="pt-6" data-id="879065vrd" data-path="src/components/ai/ContentApproval.tsx">
            <div className="flex items-center gap-3" data-id="9x4zii1k6" data-path="src/components/ai/ContentApproval.tsx">
              <div className="p-2 bg-blue-100 rounded-lg" data-id="7622vdch9" data-path="src/components/ai/ContentApproval.tsx">
                <Presentation className="h-5 w-5 text-blue-600" data-id="y5bt8yqcr" data-path="src/components/ai/ContentApproval.tsx" />
              </div>
              <div data-id="lvfd9xjcl" data-path="src/components/ai/ContentApproval.tsx">
                <p className="text-sm text-gray-600" data-id="xo7gmewmi" data-path="src/components/ai/ContentApproval.tsx">Total Slides</p>
                <p className="text-2xl font-bold text-blue-600" data-id="zmklvokol" data-path="src/components/ai/ContentApproval.tsx">{slides.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-id="qd105iq2q" data-path="src/components/ai/ContentApproval.tsx">
          <CardContent className="pt-6" data-id="5lbt5vt8s" data-path="src/components/ai/ContentApproval.tsx">
            <div className="flex items-center gap-3" data-id="eiv9oskxa" data-path="src/components/ai/ContentApproval.tsx">
              <div className="p-2 bg-green-100 rounded-lg" data-id="max8114ub" data-path="src/components/ai/ContentApproval.tsx">
                <Clock className="h-5 w-5 text-green-600" data-id="lze8hfvw8" data-path="src/components/ai/ContentApproval.tsx" />
              </div>
              <div data-id="ju45yed7e" data-path="src/components/ai/ContentApproval.tsx">
                <p className="text-sm text-gray-600" data-id="rw7ztqof7" data-path="src/components/ai/ContentApproval.tsx">Est. Duration</p>
                <p className="text-2xl font-bold text-green-600" data-id="ixh269ubg" data-path="src/components/ai/ContentApproval.tsx">{estimatedDuration}m</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-id="eb0pnc8kj" data-path="src/components/ai/ContentApproval.tsx">
          <CardContent className="pt-6" data-id="jzaxpmuzg" data-path="src/components/ai/ContentApproval.tsx">
            <div className="flex items-center gap-3" data-id="32nejesz9" data-path="src/components/ai/ContentApproval.tsx">
              <div className="p-2 bg-purple-100 rounded-lg" data-id="x482snkry" data-path="src/components/ai/ContentApproval.tsx">
                <BookOpen className="h-5 w-5 text-purple-600" data-id="b5dtgafb0" data-path="src/components/ai/ContentApproval.tsx" />
              </div>
              <div data-id="ftct0ch0w" data-path="src/components/ai/ContentApproval.tsx">
                <p className="text-sm text-gray-600" data-id="y07wmz67l" data-path="src/components/ai/ContentApproval.tsx">Word Count</p>
                <p className="text-2xl font-bold text-purple-600" data-id="m4l30itmb" data-path="src/components/ai/ContentApproval.tsx">{wordCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-id="vxdhsalba" data-path="src/components/ai/ContentApproval.tsx">
          <CardContent className="pt-6" data-id="4kyc6wj7f" data-path="src/components/ai/ContentApproval.tsx">
            <div className="flex items-center gap-3" data-id="msvsdu4la" data-path="src/components/ai/ContentApproval.tsx">
              <div className="p-2 bg-orange-100 rounded-lg" data-id="n7sfj133t" data-path="src/components/ai/ContentApproval.tsx">
                <Users className="h-5 w-5 text-orange-600" data-id="0xbep97zd" data-path="src/components/ai/ContentApproval.tsx" />
              </div>
              <div data-id="e8454mb4t" data-path="src/components/ai/ContentApproval.tsx">
                <p className="text-sm text-gray-600" data-id="ob6yuv1fn" data-path="src/components/ai/ContentApproval.tsx">Grade Level</p>
                <p className="text-lg font-bold text-orange-600" data-id="x6k3y3a8h" data-path="src/components/ai/ContentApproval.tsx">{presentationData.gradeLevel || 'Not set'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Presentation Overview */}
      <Card data-id="lq305zw6v" data-path="src/components/ai/ContentApproval.tsx">
        <CardHeader data-id="w8ue5l723" data-path="src/components/ai/ContentApproval.tsx">
          <CardTitle className="flex items-center gap-2" data-id="37hohz6xl" data-path="src/components/ai/ContentApproval.tsx">
            <Eye className="h-5 w-5 text-blue-600" data-id="dpfq6oc9u" data-path="src/components/ai/ContentApproval.tsx" />
            Presentation Overview
          </CardTitle>
        </CardHeader>
        <CardContent data-id="a4s5nu56j" data-path="src/components/ai/ContentApproval.tsx">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="akvhetuxh" data-path="src/components/ai/ContentApproval.tsx">
            <div data-id="a7sd2c8qd" data-path="src/components/ai/ContentApproval.tsx">
              <h4 className="font-medium mb-2" data-id="730llqrvq" data-path="src/components/ai/ContentApproval.tsx">Basic Information</h4>
              <div className="space-y-2" data-id="i6d6whuev" data-path="src/components/ai/ContentApproval.tsx">
                <div data-id="x3t2yv5ad" data-path="src/components/ai/ContentApproval.tsx">
                  <Badge variant="outline" className="mr-2" data-id="oo8vbulv4" data-path="src/components/ai/ContentApproval.tsx">Title</Badge>
                  <span className="text-sm" data-id="o568a5pza" data-path="src/components/ai/ContentApproval.tsx">{presentationData.title}</span>
                </div>
                <div data-id="6oi9l913p" data-path="src/components/ai/ContentApproval.tsx">
                  <Badge variant="outline" className="mr-2" data-id="2bsx7pd6x" data-path="src/components/ai/ContentApproval.tsx">Topic</Badge>
                  <span className="text-sm" data-id="pv7mhhiwt" data-path="src/components/ai/ContentApproval.tsx">{presentationData.topic}</span>
                </div>
                <div data-id="f7l3zbyt4" data-path="src/components/ai/ContentApproval.tsx">
                  <Badge variant="outline" className="mr-2" data-id="1wxbpum4a" data-path="src/components/ai/ContentApproval.tsx">Subject</Badge>
                  <span className="text-sm" data-id="w1e9gc5dx" data-path="src/components/ai/ContentApproval.tsx">{presentationData.subject}</span>
                </div>
              </div>
            </div>
            <div data-id="2nafvvluh" data-path="src/components/ai/ContentApproval.tsx">
              <h4 className="font-medium mb-2" data-id="jy7ex3ro6" data-path="src/components/ai/ContentApproval.tsx">Content Details</h4>
              <div className="space-y-2" data-id="pewylbj4s" data-path="src/components/ai/ContentApproval.tsx">
                {presentationData.learningObjectives &&
                <div data-id="tws1qsrzo" data-path="src/components/ai/ContentApproval.tsx">
                    <Badge variant="outline" className="mr-2" data-id="vxulvd1ix" data-path="src/components/ai/ContentApproval.tsx">Objectives</Badge>
                    <span className="text-sm" data-id="mogo5xzvu" data-path="src/components/ai/ContentApproval.tsx">{presentationData.learningObjectives.substring(0, 100)}...</span>
                  </div>
                }
                {presentationData.targetAudience &&
                <div data-id="punr5wdya" data-path="src/components/ai/ContentApproval.tsx">
                    <Badge variant="outline" className="mr-2" data-id="rx20a4c5q" data-path="src/components/ai/ContentApproval.tsx">Audience</Badge>
                    <span className="text-sm" data-id="xukx4wenm" data-path="src/components/ai/ContentApproval.tsx">{presentationData.targetAudience}</span>
                  </div>
                }
                <div data-id="qmthfwmi2" data-path="src/components/ai/ContentApproval.tsx">
                  <Badge variant="outline" className="mr-2" data-id="ppdp1u5eo" data-path="src/components/ai/ContentApproval.tsx">Template</Badge>
                  <span className="text-sm" data-id="q1cywlnff" data-path="src/components/ai/ContentApproval.tsx">{presentationData.template?.name || 'Default'}</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Slide Preview */}
      <Card data-id="y0nv4ff5j" data-path="src/components/ai/ContentApproval.tsx">
        <CardHeader data-id="y8f47jx60" data-path="src/components/ai/ContentApproval.tsx">
          <div className="flex items-center justify-between" data-id="7wgafrsyf" data-path="src/components/ai/ContentApproval.tsx">
            <CardTitle className="flex items-center gap-2" data-id="nsb765r3t" data-path="src/components/ai/ContentApproval.tsx">
              <Play className="h-5 w-5 text-green-600" data-id="whn46w1ed" data-path="src/components/ai/ContentApproval.tsx" />
              Slide Preview
            </CardTitle>
            <div className="flex items-center gap-2" data-id="u33c3xds5" data-path="src/components/ai/ContentApproval.tsx">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setCurrentSlideIndex(Math.max(0, currentSlideIndex - 1))}
                disabled={currentSlideIndex === 0} data-id="xifmngb62" data-path="src/components/ai/ContentApproval.tsx">

                ←
              </Button>
              <Badge variant="outline" data-id="5irmkec0i" data-path="src/components/ai/ContentApproval.tsx">
                {currentSlideIndex + 1} of {slides.length}
              </Badge>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setCurrentSlideIndex(Math.min(slides.length - 1, currentSlideIndex + 1))}
                disabled={currentSlideIndex === slides.length - 1} data-id="ixberg7sv" data-path="src/components/ai/ContentApproval.tsx">

                →
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent data-id="6lyijja8i" data-path="src/components/ai/ContentApproval.tsx">
          {currentSlide &&
          <motion.div
            key={currentSlideIndex}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-4" data-id="88h0oe0zh" data-path="src/components/ai/ContentApproval.tsx">

              <div className="flex items-center gap-2 mb-4" data-id="9dqiqwmja" data-path="src/components/ai/ContentApproval.tsx">
                <Badge variant="secondary" data-id="z0eyhhljb" data-path="src/components/ai/ContentApproval.tsx">{currentSlide.type}</Badge>
                <span className="text-sm text-gray-600" data-id="9ct2ik0p7" data-path="src/components/ai/ContentApproval.tsx">Slide {currentSlideIndex + 1}</span>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-6 border" data-id="1k2l3m4kx" data-path="src/components/ai/ContentApproval.tsx">
                <h3 className="text-xl font-bold mb-4 text-gray-900" data-id="299i20b9t" data-path="src/components/ai/ContentApproval.tsx">
                  {getTextContent(currentSlide.title)}
                </h3>
                <div className="prose prose-sm max-w-none" data-id="i1kdfzetv" data-path="src/components/ai/ContentApproval.tsx">
                  <p className="text-gray-700 whitespace-pre-wrap" data-id="84p1lrki1" data-path="src/components/ai/ContentApproval.tsx">
                    {getTextContent(currentSlide.content)}
                  </p>
                </div>
                
                {currentSlide.interactiveElement &&
              <div className="mt-4 p-3 bg-blue-50 rounded border-l-4 border-blue-400" data-id="926ysuv8s" data-path="src/components/ai/ContentApproval.tsx">
                    <h5 className="font-medium text-blue-900 mb-1" data-id="wpi6i7oir" data-path="src/components/ai/ContentApproval.tsx">Interactive Element</h5>
                    <p className="text-sm text-blue-800" data-id="11g4oc75i" data-path="src/components/ai/ContentApproval.tsx">{getTextContent(currentSlide.interactiveElement)}</p>
                  </div>
              }
              </div>

              {currentSlide.notes &&
            <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200" data-id="3csdcpt18" data-path="src/components/ai/ContentApproval.tsx">
                  <h5 className="font-medium text-yellow-900 mb-2 flex items-center gap-2" data-id="zomer64cp" data-path="src/components/ai/ContentApproval.tsx">
                    <BookOpen className="h-4 w-4" data-id="ddyafb0lp" data-path="src/components/ai/ContentApproval.tsx" />
                    Speaker Notes
                  </h5>
                  <p className="text-sm text-yellow-800" data-id="0fvgbuzwx" data-path="src/components/ai/ContentApproval.tsx">{getTextContent(currentSlide.notes)}</p>
                </div>
            }
            </motion.div>
          }
        </CardContent>
      </Card>

      {/* Slide Summary */}
      <Card data-id="7fxcimqfb" data-path="src/components/ai/ContentApproval.tsx">
        <CardHeader data-id="ow4vetox1" data-path="src/components/ai/ContentApproval.tsx">
          <CardTitle data-id="sbm82a5y4" data-path="src/components/ai/ContentApproval.tsx">All Slides Summary</CardTitle>
        </CardHeader>
        <CardContent data-id="tsyewjvb4" data-path="src/components/ai/ContentApproval.tsx">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-id="suhr5vvvn" data-path="src/components/ai/ContentApproval.tsx">
            {slides.map((slide, index) =>
            <div
              key={slide.id}
              className={`p-3 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
              currentSlideIndex === index ? 'bg-blue-50 border-blue-300' : 'hover:bg-gray-50'}`
              }
              onClick={() => setCurrentSlideIndex(index)} data-id="t2phrk73v" data-path="src/components/ai/ContentApproval.tsx">

                <div className="flex items-center justify-between mb-2" data-id="kz7hmkexl" data-path="src/components/ai/ContentApproval.tsx">
                  <Badge variant="outline" className="text-xs" data-id="rar1fodee" data-path="src/components/ai/ContentApproval.tsx">
                    {index + 1}
                  </Badge>
                  <Badge variant="secondary" className="text-xs" data-id="nwpp4lgpp" data-path="src/components/ai/ContentApproval.tsx">
                    {slide.type}
                  </Badge>
                </div>
                <h5 className="font-medium text-sm line-clamp-1 mb-1" data-id="289ae9ulo" data-path="src/components/ai/ContentApproval.tsx">
                  {getTextContent(slide.title)}
                </h5>
                <p className="text-xs text-gray-600 line-clamp-2" data-id="yk1mioj16" data-path="src/components/ai/ContentApproval.tsx">
                  {getTextContent(slide.content)}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Action buttons */}
      <div className="flex items-center justify-between pt-6 border-t" data-id="rtn6kx9oz" data-path="src/components/ai/ContentApproval.tsx">
        <div className="flex items-center gap-3" data-id="b3b86mx2t" data-path="src/components/ai/ContentApproval.tsx">
          <Button onClick={onPrevious} variant="outline" data-id="tznqfxfum" data-path="src/components/ai/ContentApproval.tsx">
            <ArrowLeft className="h-4 w-4 mr-2" data-id="zywrvvybj" data-path="src/components/ai/ContentApproval.tsx" />
            Back to Edit
          </Button>
          <Button onClick={handleExportSummary} variant="outline" data-id="3vs7kayfi" data-path="src/components/ai/ContentApproval.tsx">
            <Download className="h-4 w-4 mr-2" data-id="dsf9xxi2q" data-path="src/components/ai/ContentApproval.tsx" />
            Export Summary
          </Button>
          <Button
            onClick={handleSavePresentation}
            variant="outline"
            disabled={isSaving} data-id="4a40r0rpj" data-path="src/components/ai/ContentApproval.tsx">

            {isSaving ?
            <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" data-id="6fbdduuaj" data-path="src/components/ai/ContentApproval.tsx" />
                Saving...
              </> :

            <>
                <Save className="h-4 w-4 mr-2" data-id="jeqh3u2sf" data-path="src/components/ai/ContentApproval.tsx" />
                Save Draft
              </>
            }
          </Button>
        </div>

        <div className="flex items-center gap-3" data-id="6lthxna8u" data-path="src/components/ai/ContentApproval.tsx">
          {isApproved &&
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex items-center gap-2 text-green-600" data-id="kemy2yv8n" data-path="src/components/ai/ContentApproval.tsx">

              <CheckCircle className="h-5 w-5" data-id="qbp7omvm4" data-path="src/components/ai/ContentApproval.tsx" />
              <span className="text-sm font-medium" data-id="3znnfh56m" data-path="src/components/ai/ContentApproval.tsx">Content Approved</span>
            </motion.div>
          }
          <Button
            onClick={handleApproveAndContinue}
            disabled={isApproved}
            className="flex items-center gap-2 bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700" data-id="3l584gznr" data-path="src/components/ai/ContentApproval.tsx">

            {isApproved ? 'Proceeding...' : 'Approve & Continue'}
            <ArrowRight className="h-4 w-4" data-id="e7ebsto7v" data-path="src/components/ai/ContentApproval.tsx" />
          </Button>
        </div>
      </div>
    </div>);

};

export default ContentApproval;